import { IProduct } from 'apis/algolia';
import { productIsEligibleForUser } from 'utils/marketplace';
import useQueryAlgoliaProductsById from './useQueryAlgoliaProductsById';
import useQueryMisoSemanticSearch from './useQueryMisoSemanticSearch';
import { IEmployee } from './useQueryProfile';

type UseProductToProductRecommendationsDeps = {
  query: string;
  enabled: boolean;
  employee: IEmployee;
};

const useSemanticProductSearch = ({
  query,
  enabled,
  employee,
}: UseProductToProductRecommendationsDeps): {
  isLoading: boolean;
  products?: Array<IProduct>;
} => {
  const { data: miso_search_results, isLoading: isLoadingMiso } =
    useQueryMisoSemanticSearch({
      enabled: Boolean(enabled && query),
      query,
      onError: console.error,
    });

  const product_ids = miso_search_results?.map((rec) => rec.product_id) || [];

  // Our canonical data source for product listings is Algolia. Miso
  // has the ability to match this interface, or likely does, but
  // since we're still early in trialling it as a product, we haven't
  // taken the time to set up our data so that it actually does match.
  // Thus, in order to populate product cards, we need to take the list
  // of recommended IDs that we get from Miso & ask for those specific
  // products from Algolia.
  // Should we continue to make use of Miso, we should _really_ set our
  // Miso product catalog up such that the products it returns to us
  // conform to the same interface that Algolia products do. Once that's
  // done, we should be able to remove this intermediate step of grabbing
  // data from Algolia.
  const { data, isLoading: isLoadingAlgolia } = useQueryAlgoliaProductsById({
    product_ids,
    enabled: Boolean(product_ids.length),
    onError: console.error,
  });

  return {
    isLoading: isLoadingMiso || isLoadingAlgolia,
    products: data?.filter((product) =>
      // Our data is also not set up in Miso such that we can accurately
      // filter it when making requests. Nor can we filter when getting
      // products from Algolia by ID (at least I don't think we can).
      // So, we need to do a little bit of client-side filtering.
      // We may be able to do this filtering when querying Miso later if
      // we improve our product catalog data there.
      productIsEligibleForUser(product, employee)
    ),
  };
};

export default useSemanticProductSearch;
